"use client";

import { useAtomValue } from "jotai";
import { type FunctionComponent, memo, useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";
import { useCurrentArticleIds } from "scmp-app/lib/current-item";
import { useFloatingButtonConfig } from "scmp-app/lib/floating-button/hooks";

import CloseIcon from "./close-icon.svg";
import LinkIcon from "./link-icon.svg";
import {
  CloseButton,
  FloatingButtonIcon,
  FloatingButtonWrapper,
  PopupContainer,
  PopupIframe,
  PopupOverlay,
  TooltipLabel,
} from "./styles";
import PollIcon from "./survey-icon.svg";

const FLOATING_BUTTON_AUTO_OPEN = "floating_button_auto_open";

type Props = {
  entityId: string;
};

export const FloatingButtonComponent: FunctionComponent<Props> = ({ entityId }) => {
  const { hideLabelInMobile, isEnable, tooltipLabel, type, typeformLink } =
    useFloatingButtonConfig(entityId);
  const isShowTakeoverAdSlot = Boolean(useAtomValue(isShowTakeoverAdSlotAtom));
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const { currentArticleId } = useCurrentArticleIds();

  const isCurrentArticle = entityId === currentArticleId;

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const takeoverOffset = isShowTakeoverAdSlot ? (isDesktopUp ? "120px" : "80px") : undefined;

  useEffect(() => {
    if (!isEnable || !typeformLink || !isCurrentArticle || isPopupOpen) return;
    if (type !== "popup") return;

    const searchParameters = new URLSearchParams(window.location.search);
    if (searchParameters.get(FLOATING_BUTTON_AUTO_OPEN) === "1") {
      setIsPopupOpen(true);

      const url = new URL(window.location.href);
      url.searchParams.delete(FLOATING_BUTTON_AUTO_OPEN);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }, [isEnable, typeformLink, isCurrentArticle, isPopupOpen, type]);

  useEffect(() => {
    document.body.style.overflow = isPopupOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isPopupOpen]);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    if (type === "link") {
      window.open(typeformLink, "_blank", "noopener,noreferrer");
    } else {
      setIsPopupOpen(true);
    }
  }, [type, typeformLink]);

  if (!isEnable || !typeformLink || !isCurrentArticle) return null;

  return (
    <ErrorBoundary fallback={<></>}>
      <ClientSideSuspense fallback={<></>}>
        <FloatingButtonWrapper
          $hasTooltip={Boolean(tooltipLabel)}
          $hideLabelInMobile={hideLabelInMobile}
          $takeoverOffset={takeoverOffset}
          $type={type}
          onClick={handleClick}
        >
          {tooltipLabel && (
            <TooltipLabel
              $hideLabelInMobile={hideLabelInMobile}
              dangerouslySetInnerHTML={{ __html: tooltipLabel }}
            />
          )}
          <FloatingButtonIcon $type={type} aria-label={type === "link" ? "Open link" : "Open poll"}>
            {type === "link" ? <LinkIcon /> : <PollIcon />}
          </FloatingButtonIcon>
        </FloatingButtonWrapper>
        {type === "popup" && isPopupOpen && (
          <PopupOverlay>
            <PopupContainer>
              <CloseButton aria-label="Close poll" onClick={handleClosePopup}>
                <CloseIcon />
              </CloseButton>
              <PopupIframe src={typeformLink} title="Article Poll" />
            </PopupContainer>
          </PopupOverlay>
        )}
      </ClientSideSuspense>
    </ErrorBoundary>
  );
};

export const FloatingButton = memo(FloatingButtonComponent);
